import { Alert, Button, Stack } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import { EmailSentDialog } from '@components/LimitedAccessBanner/EmailSentDialog';
import { UnstyledLink } from '@components/UnstyledLink';
import { useNotification } from '@utils/useNotification';
import { useResponsive } from '@utils/useResponsive';
import { useGetLoginInfoQuery } from 'gql/index';

const loginPath = (tenantId: string, returnUrl: string) =>
  `/${tenantId}/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`;

export const LimitedAccessBanner: React.FC = () => {
  const { formatMessage } = useIntl();
  const { isMobile } = useResponsive();
  const { tenantId } = useParams();
  const location = useLocation();
  const { notifyFailure } = useNotification();
  const [requestingAccess, setRequestingAccess] = React.useState(false);
  const [emailSentDialogOpen, setEmailSentDialogOpen] = React.useState(false);

  const { data: loginInfo } = useGetLoginInfoQuery(undefined, { select: d => d.loginInfo });

  if (tenantId == null) return null;

  const returnUrl = `${location.pathname}${location.search}`;
  const loginUrl = loginPath(tenantId, returnUrl);
  const hasSignedUp = loginInfo?.hasSignedUp === true;

  const handleRequestFullAccess = async () => {
    if (requestingAccess) return;
    setRequestingAccess(true);
    try {
      const res = await fetch(`/${tenantId}/api/Auth/RequestFullAccess`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        setEmailSentDialogOpen(true);
      } else {
        notifyFailure(formatMessage({ id: 'An unexpected error has occurred' }));
      }
    } catch {
      notifyFailure(formatMessage({ id: 'An unexpected error has occurred' }));
    } finally {
      setRequestingAccess(false);
    }
  };

  const title = formatMessage({
    id: 'Limited access via secure link'
  });
  const description = hasSignedUp ? formatMessage({
    id: 'You can only perform the requested action. Please sign in to access other project resources.'
  }) : formatMessage({
    id: 'You can only perform the requested action. Please create an account to access other project resources.'
  });

  const connectLabel = formatMessage({ id: 'Connect' });
  const createAccountLabel = formatMessage({ id: 'Create account' });

  const ctaButton = hasSignedUp ? (
    <UnstyledLink to={loginUrl}>
      <Button variant="contained" color="primary" size="small">
        {connectLabel}
      </Button>
    </UnstyledLink>
  ) : (
    <Button variant="contained" color="primary" size="small" onClick={handleRequestFullAccess} disabled={requestingAccess}>
      {createAccountLabel}
    </Button>
  );

  if (isMobile) {
    return (
      <>
      <EmailSentDialog open={emailSentDialogOpen} onClose={() => setEmailSentDialogOpen(false)} />
      <Alert
        severity="info"
        icon={false}
        sx={{
          width: '100%',
          '& .MuiAlert-message': { width: '100%' }
        }}
      >
        <Stack direction="column" spacing={1.5} width="100%">
          <Stack direction="row" alignItems="center" gap={0.5}>
            <span aria-hidden>{'\uD83D\uDD12'}</span>
            <strong>{formatMessage({ id: 'Limited access' })}</strong>
          </Stack>
          <Stack direction="row" spacing={1} width="100%">
            {hasSignedUp ? (
              <UnstyledLink to={loginUrl} sx={{ flex: 1 }}>
                <Button variant="contained" color="primary" fullWidth size="small">
                  {connectLabel}
                </Button>
              </UnstyledLink>
            ) : (
              <Button variant="contained" color="primary" fullWidth size="small" onClick={handleRequestFullAccess} disabled={requestingAccess}>
                {createAccountLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      </Alert>
      </>
    );
  }

  return (
    <>
    <EmailSentDialog open={emailSentDialogOpen} onClose={() => setEmailSentDialogOpen(false)} />
    <Alert
      severity="info"
      icon={false}
      sx={{
        width: '100%',
        '& .MuiAlert-message': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          width: '100%'
        }
      }}
    >
      <Stack direction="row" alignItems="center" flex={1} flexWrap="wrap" gap={0.5} minWidth={0}>
        <span aria-hidden>{'\uD83D\uDD12'}</span> {/* lock emoji for limited access */}
        <strong>{title}</strong>
        <span>{description}</span>
      </Stack>
      <Stack direction="row" alignItems="center" gap={1} flexShrink={0}>
        {ctaButton}
      </Stack>
    </Alert>
    </>
  );
};
